import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { PropFirmWithPrograms } from '@/types';
import { FirmTableRow } from '../comparison/FirmTableRow';
import AccountSizeSelector from '../comparison/AccountSizeSelector';

interface PropFirmComparisonTableProps {
  firms: PropFirmWithPrograms[];
  loading: boolean;
  initialAccountSize?: string;
}

export default function PropFirmComparisonTable({ firms, loading, initialAccountSize }: PropFirmComparisonTableProps) {
  const [selectedAccountSize, setSelectedAccountSize] = useState<string>(initialAccountSize ?? '50k');

  // Filter firms that have programs matching the selected account size
  const firmsWithSelectedSize = firms.filter(firm => 
    firm.funding_programs?.some(p => p.account_size === parseInt(selectedAccountSize.replace('k', '000')))
  );

  // Sort by effective price (discounted_price if set, else base_price), lowest first
  const sortedFirms = [...firmsWithSelectedSize].sort((a, b) => {
    const size = parseInt(selectedAccountSize.replace('k', '000'));
    const aProgram = a.funding_programs?.find(p => p.account_size === size);
    const bProgram = b.funding_programs?.find(p => p.account_size === size);
    const aPrice = aProgram?.discounted_price ?? aProgram?.base_price ?? Infinity;
    const bPrice = bProgram?.discounted_price ?? bProgram?.base_price ?? Infinity;
    return aPrice - bPrice;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AccountSizeSelector
          selectedSize={selectedAccountSize}
          onSizeChange={setSelectedAccountSize}
          className="w-[140px]"
        />
      </div>
      
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700/50">
        <Table className="w-full">
          <TableHeader className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-[#1C2644] dark:to-[#1C2644]">
            <TableRow>
              <TableHead className="text-white font-medium w-[200px] border-r border-white/10">Firm</TableHead>
              <TableHead className="text-white font-medium w-[150px] border-r border-white/10">% Off / Code</TableHead>
              <TableHead className="text-white font-medium w-[150px] border-r border-white/10">
                Price
                <div className="text-sm font-normal opacity-75">(Discounted)</div>
              </TableHead>
              <TableHead className="text-white font-medium border-r border-white/10">
                Profit Target / Drawdown
              </TableHead>
              <TableHead className="text-white font-medium border-r border-white/10">
                Evaluation Rules
              </TableHead>
              <TableHead className="text-white font-medium border-r border-white/10 w-[100px]">
                Min. Days to Pass
              </TableHead>
              <TableHead className="text-white font-medium border-r border-white/10">
                Payout & Timeline
              </TableHead>
              <TableHead className="text-white font-medium text-center w-[120px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white dark:bg-gray-900/50">
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                    <span>Loading firms...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : sortedFirms.length > 0 ? (
              sortedFirms.map((firm, index) => (
                <FirmTableRow 
                  key={firm.id} 
                  firm={firm}
                  selectedAccountSize={selectedAccountSize}
                  index={index}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  No firms found with {selectedAccountSize} account size
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        * Some links on this page may be affiliate links. We may earn a commission if you use these links.
      </div>
    </div>
  );
}
